import { Component, OnInit } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { ActivatedRoute } from '@angular/router';
import { take } from 'rxjs';

import { SalesOrderLineItemsAssemblyOrderDialogComponent } from 'v2/modules/sales-order/components/sales-order-line-items-assembly-order-dialog/sales-order-line-items-assembly-order-dialog.component';
import { SalesOrderLineItemsTransferOrderDialogComponent } from 'v2/modules/sales-order/components/sales-order-line-items-transfer-order-dialog/sales-order-line-items-transfer-order-dialog.component';
import { DialogConfig, SnackBarConfig } from 'v2/modules/shared';
import {
  IOrderActions,
  IOrderData,
  IOrderRowData,
} from 'v2/modules/shared/interfaces/order.interface';
import { AssemblyOrderApiService } from 'v2/modules/shared/services/api/assembly-order-api.service';

import { OrderSupplierPurchaseShipmentService } from './../../../shared/services/api/order/order-supplier-purchase-shipment.service';
import { OrderTransferApiService } from './../../../shared/services/api/order/order-transfer-api.service';
import { EAssemblyOrderStatus } from './../../interfaces/assembly-order.interface';

@Component({
  selector: 'lum-assembly-order-list',
  templateUrl: './assembly-order-list.component.html',
  styleUrls: ['./assembly-order-list.component.scss'],
})
export class AssemblyOrderListComponent implements OnInit {
  closedOrdersTabViewed :boolean = false;
  status :EAssemblyOrderStatus;
  selectedTabIndex :number = 0;
  lineItemRowData :IOrderRowData;
  orderItemActions :IOrderActions[] = [
    {
      label: 'Create Purchase Order',
      onRowClick: (data :any) => this.onCreatePurchaseOrder(data),
    },
    {
      label: 'Create Transfer Order',
      onRowClick: (data :any) => this.onToggleTransferOrderDialog(data),
    },
  ];

  constructor(
    private route :ActivatedRoute,
    private assemblyOrderApiService :AssemblyOrderApiService,
    private orderSupplierPurchaseShipmentService :OrderSupplierPurchaseShipmentService,
    private orderTransferApiService :OrderTransferApiService,
    private dialog :MatDialog,
    private snackBar :MatSnackBar,
  ) {}

  ngOnInit() :void {
    const status = this.route.snapshot.queryParamMap.get(
      'status',
    ) as EAssemblyOrderStatus;
    if (status) {
      switch (status) {
        case 'draft':
          this.selectedTabIndex = 0;

          break;

        case 'completed':
          this.selectedTabIndex = 1;
          this.closedOrdersTabViewed = true;
          break;

        default:
          break;
      }
    }

    this.#loadOrderLineItems();
  }

  onTabChange(index :number) :void {
    if (!this.closedOrdersTabViewed && index === 1) {
      this.closedOrdersTabViewed = true;
    }
  }

  refreshClosedOrders(refresh :boolean) :void {
    if (refresh) {
      this.closedOrdersTabViewed = false;
    }
  }

  // TO and PO only
  // TODO: Move this to on demand and separate component
  #loadOrderLineItems() {
    this.assemblyOrderApiService
      .getLineItems({
        relations: ['rfq.tags.tag', 'baseUom', 'rfq.preferredVendor'].join('|'),
        sortDirection: 'desc',
        sortField: 'created_at',
      })
      .subscribe({
        next: (response :any) => {
          const data :IOrderData[] = response?.data?.map((item :any) => ({
            id: item.id,
            orderNumber: item?.orderId,
            sku: item?.rfq?.internalSku,
            name: item?.rfq?.productName,
            quantity: item?.actualQuantity,
            uom: item?.actualUom?.name,
            stocks: item.numberOfStocks,
            tags: item?.rfq?.rfqTags?.map((tag :any) => tag.tag.name),
            supplier: item?.rfq?.preferredVendor?.factoryName,
          }));
          this.lineItemRowData = {
            data,
            meta: response?.meta,
          };
          // this.cd.detectChanges();
        },
        error: (err) => {
          this.snackBar.open('Error loading order line items', 'Dismiss', {
            duration: SnackBarConfig.ERROR.duration,
          });
        },
      });
  }

  onToggleAssemblyOrderDialog(items :any[]) :void {
    const orderItemIds = items.map((orderItem) => orderItem.id);
    this.dialog.open(SalesOrderLineItemsAssemblyOrderDialogComponent, {
      width: DialogConfig.Size['X_SMALL'],
      data: {
        orderItemIds,
      },
    });
  }

  onToggleTransferOrderDialog(items :any[]) :void {
    const orderItemIds = items.map((orderItem) => orderItem.id);
    this.dialog
      .open(SalesOrderLineItemsTransferOrderDialogComponent, {
        width: DialogConfig.Size.SMALL,
        data: {
          orderItemIds,
        },
      })
      .afterClosed()
      .pipe(take(1))
      .subscribe({
        next: (response :any) => {
          if (response) {
            this.onCreateTransferOrder({ items, warehouseId: response.id });
          }
        },
        error: (err) => {
          console.error('Error creating transfer order:', err);
          this.snackBar.open('Error creating transfer order', 'Dismiss', {
            duration: SnackBarConfig.ERROR.duration,
          });
        },
      });
  }

  onCreatePurchaseOrder(items :any[]) :void {
    const assemblyOrderItemIds = items.map((item) => item.id);
    this.snackBar.open('Creating purchase order...', 'Dismiss');
    this.orderSupplierPurchaseShipmentService
      .createPOByOrderLineItems({ assemblyOrderItemIds })
      .subscribe({
        next: (response :any) => {
          this.snackBar.open('Purchase order created successfully', 'Dismiss', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
        error: (err) => {
          console.error('Error creating purchase order:', err);
          this.snackBar.open('Error creating purchase order', 'Dismiss', {
            duration: SnackBarConfig.ERROR.duration,
          });
        },
      });
  }

  onCreateTransferOrder(data :{ items :any[]; warehouseId :number }) :void {
    const { items, warehouseId } = data;
    const orderItemIds = items.map((item) => item.id);
    this.snackBar.open('Creating transfer order...', 'Dismiss');
    this.orderTransferApiService
      .createTOFromAOLineItems({ warehouseId, orderItemIds })
      .subscribe({
        next: (response :any) => {
          this.snackBar.open('Transfer order created successfully', 'Dismiss', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
        error: (err) => {
          console.error('Error creating transfer order:', err);
          this.snackBar.open('Error creating transfer order', 'Dismiss', {
            duration: SnackBarConfig.ERROR.duration,
          });
        },
      });
  }
}
